// CrashReportModal.jsx - Crash Report Viewer
// Version: 1.0.0 (v3.95)

import { useState, useEffect } from 'react'
import {
    X, AlertTriangle, Clock, Phone, FileText, Image,
    ChevronDown, ChevronUp, Terminal, RefreshCw, Calendar
} from 'lucide-react'
import { monitoringApi } from '../services/api'
import './CrashReportModal.css'

const formatDateTime = (date) => {
    if (!date) return '-'
    const d = new Date(date)
    return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    })
}

const formatTimeAgo = (date) => {
    if (!date) return '-'
    const diff = Math.abs(Math.floor((new Date() - new Date(date)) / 1000))
    if (diff < 60) return `${diff}s ago`
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
    return `${Math.floor(diff / 86400)}d ago`
}

export default function CrashReportModal({ bot, onClose }) {
    const [reports, setReports] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [selectedDate, setSelectedDate] = useState('')
    const [expandedReport, setExpandedReport] = useState(null)
    const [expandedLog, setExpandedLog] = useState(null)
    const [selectedImage, setSelectedImage] = useState(null)

    // Fetch crash reports
    useEffect(() => {
        fetchReports()
    }, [bot.sip_id, selectedDate])

    const fetchReports = async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await monitoringApi.getCrashReports(bot.sip_id, selectedDate || undefined)
            setReports(res.data?.reports || [])
        } catch (err) {
            console.error('Failed to fetch crash reports:', err)
            setError('Failed to load crash reports')
            setReports([])
        } finally {
            setLoading(false)
        }
    }

    const toggleReport = (reportId) => {
        setExpandedReport(expandedReport === reportId ? null : reportId)
        setExpandedLog(null)
    }

    const toggleLog = (reportId) => {
        setExpandedLog(expandedLog === reportId ? null : reportId)
    }

    return (
        <div className="cr-modal-overlay" onClick={onClose}>
            <div className="cr-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="cr-header">
                    <div className="cr-header-left">
                        <AlertTriangle size={18} />
                        <h3>Crash Reports - {bot.sip_id}</h3>
                    </div>
                    <div className="cr-header-right">
                        <input
                            type="date"
                            className="cr-date-filter"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            title="Filter by date"
                        />
                        <button className="cr-refresh-btn" onClick={fetchReports} disabled={loading}>
                            <RefreshCw size={14} className={loading ? 'spinning' : ''} />
                        </button>
                        <button className="cr-close-btn" onClick={onClose}>
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="cr-content">
                    {loading ? (
                        <div className="cr-loading">
                            <div className="cr-spinner" />
                            <span>Loading crash reports...</span>
                        </div>
                    ) : error ? (
                        <div className="cr-error">
                            <AlertTriangle size={24} />
                            <span>{error}</span>
                        </div>
                    ) : reports.length === 0 ? (
                        <div className="cr-empty">
                            <FileText size={32} />
                            <span>No crash reports found</span>
                            <span className="cr-empty-hint">
                                {selectedDate ? 'Try selecting a different date' : 'This bot has no recorded crashes'}
                            </span>
                        </div>
                    ) : (
                        <div className="cr-reports-list">
                            {reports.map(report => (
                                <div key={report.id} className={`cr-report ${expandedReport === report.id ? 'expanded' : ''}`}>
                                    {/* Report Header */}
                                    <div className="cr-report-header" onClick={() => toggleReport(report.id)}>
                                        <div className="cr-report-time">
                                            <Clock size={14} />
                                            <span>{formatDateTime(report.crash_time)}</span>
                                            <span className="cr-time-ago">({formatTimeAgo(report.crash_time)})</span>
                                        </div>
                                        <div className="cr-report-summary">
                                            {report.call_status && (
                                                <span className="cr-tag cr-tag-call">
                                                    <Phone size={10} /> {report.call_status}
                                                </span>
                                            )}
                                            {report.current_step && (
                                                <span className="cr-tag cr-tag-step">
                                                    Step {report.current_step}
                                                </span>
                                            )}
                                            {report.revive_type && (
                                                <span className={`cr-tag cr-tag-revive ${report.revive_type}`}>
                                                    {report.revive_type === 'auto' ? 'Auto-revived' : 'Manual'}
                                                </span>
                                            )}
                                        </div>
                                        <div className="cr-report-expand">
                                            {expandedReport === report.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                        </div>
                                    </div>

                                    {/* Report Details (Expanded) */}
                                    {expandedReport === report.id && (
                                        <div className="cr-report-details">
                                            {/* Info Grid */}
                                            <div className="cr-info-grid">
                                                <div className="cr-info-item">
                                                    <span className="cr-info-label">Crash Time</span>
                                                    <span className="cr-info-value">{formatDateTime(report.crash_time)}</span>
                                                </div>
                                                {report.revive_time && (
                                                    <div className="cr-info-item">
                                                        <span className="cr-info-label">Revive Time</span>
                                                        <span className="cr-info-value">{formatDateTime(report.revive_time)}</span>
                                                    </div>
                                                )}
                                                {report.phone_number && (
                                                    <div className="cr-info-item">
                                                        <span className="cr-info-label">Phone</span>
                                                        <span className="cr-info-value">{report.phone_number}</span>
                                                    </div>
                                                )}
                                                {report.call_id && (
                                                    <div className="cr-info-item">
                                                        <span className="cr-info-label">Call ID</span>
                                                        <span className="cr-info-value cr-info-mono">{report.call_id}</span>
                                                    </div>
                                                )}
                                                <div className="cr-info-item">
                                                    <span className="cr-info-label">Op State</span>
                                                    <span className="cr-info-value">{report.operational_state || '-'}</span>
                                                </div>
                                            </div>

                                            {/* Error Log */}
                                            {report.error_log && (
                                                <div className="cr-section">
                                                    <div className="cr-section-header">
                                                        <AlertTriangle size={14} />
                                                        <span>Error</span>
                                                    </div>
                                                    <div className="cr-error-log">{report.error_log}</div>
                                                </div>
                                            )}

                                            {/* Screenshots */}
                                            {(report.screenshot_1 || report.screenshot_2 || report.screenshot_3) && (
                                                <div className="cr-section">
                                                    <div className="cr-section-header">
                                                        <Image size={14} />
                                                        <span>Screenshots</span>
                                                    </div>
                                                    <div className="cr-screenshots">
                                                        {[1, 2, 3].map(num => {
                                                            const screenshot = report[`screenshot_${num}`]
                                                            const trigger = report[`screenshot_${num}_trigger`]
                                                            if (!screenshot) return null

                                                            return (
                                                                <div
                                                                    key={num}
                                                                    className="cr-screenshot-thumb"
                                                                    onClick={() => setSelectedImage({ src: screenshot, trigger })}
                                                                    title={trigger || `Screenshot ${num}`}
                                                                >
                                                                    <img src={screenshot} alt={`Screenshot ${num}`} />
                                                                    {trigger && <span className="cr-screenshot-label">{trigger}</span>}
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Console Log (Collapsible) */}
                                            {report.console_log && (
                                                <div className="cr-section">
                                                    <div
                                                        className="cr-section-header cr-clickable"
                                                        onClick={() => toggleLog(report.id)}
                                                    >
                                                        <Terminal size={14} />
                                                        <span>Console Log</span>
                                                        <span className="cr-log-hint">(last 200 lines)</span>
                                                        {expandedLog === report.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                                    </div>
                                                    {expandedLog === report.id && (
                                                        <pre className="cr-console-log">{report.console_log}</pre>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Image Lightbox */}
                {selectedImage && (
                    <div className="cr-lightbox" onClick={() => setSelectedImage(null)}>
                        <div className="cr-lightbox-content" onClick={e => e.stopPropagation()}>
                            <button className="cr-lightbox-close" onClick={() => setSelectedImage(null)}>
                                <X size={24} />
                            </button>
                            <img src={selectedImage.src} alt="Screenshot" />
                            {selectedImage.trigger && (
                                <div className="cr-lightbox-caption">{selectedImage.trigger}</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
